<script setup lang="ts">
//#region import
import { ref, useTemplateRef, watch } from 'vue';
//#endregion

//#region type
export interface Props {
  value: string,
  isMove?: boolean,
  isDelete?: boolean,
}

export interface Emits {
  'update': [value: string],
  'delete': [],
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  isMove: () => false,
  isDelete: () => true,
});

const emit = defineEmits<Emits>();
//#endregion

//#region state
const fieldState = ref('display');

const contenteditableRef = useTemplateRef('contenteditable');
//#endregion

//#region methods
function contentFocus() {
  fieldState.value = 'editor';
}

function contentBlur() {
  fieldState.value = 'display';
  if(contenteditableRef.value) {
    contenteditableRef.value.innerHTML = contenteditableRef.value.innerText;
    emit('update', contenteditableRef.value.innerText);
  }
}

function handleEsc() {
  if(contenteditableRef.value) {
    contenteditableRef.value.innerText = props.value;
    contenteditableRef.value.blur();
  }
}

function deleteAction() {
  emit('delete');
}
//#endregion

//#region watch 
watch(() => props.value, (value) => {
  if(contenteditableRef.value) {
    contenteditableRef.value.innerText = value;
  }
});
//#endregion
</script>

<template>
  <div class="editable-header t:space-x-12">
    <UiIcon
      v-if="isMove"
      :size="16"
      name="bars"
    />
    <div
      ref="contenteditable"
      class=""
      :class="[
        'editable-header__contenteditable',
        `editable-header__contenteditable_${fieldState}`
      ]"
      :contenteditable="true"
      :spellcheck="false"
      @keypress.enter.prevent="contenteditableRef!.blur"
      @keydown.esc="handleEsc"
      @focus="contentFocus"
      @blur="contentBlur"
    >
      {{ value }}
    </div>
    <ActionIcon
      v-if="isDelete && fieldState !== 'editor'"
      icon="trash-alt"
      color="var(--text-negative)"
      :tooltip="$t('dashboards.other.delete')"
      :is-overlay="false"
      @click="deleteAction"
    />
  </div>
</template>

<style lang="scss">
.editable-header {
  display: inline-flex;
  align-items: center;

  &__contenteditable{
    display: block;
    white-space: nowrap;
    min-width: 10px;
    outline: none;
    color: var(--text-body);
    font-size: 20px;
    line-height: 1;
    font-weight: normal;
    position: relative;
    &:after {
      content: '';
      opacity: 0;
      width: 100%;
      height: 100%;
      position: absolute;
      background-color: var(--background-brand-subtle);
      border-radius: 8px;
      top: -4px;
      left: -8px;
      z-index: -1;
      padding: 4px 8px;
      box-sizing: content-box;
    }

    &_display {
      &:hover:after {
        opacity: 1;
      }
    }
    &:focus {
      border-bottom: 1px solid var(--text-positive);
    }

  }

}
</style>
